import React from 'react';
import { Container, Row, Col, Card, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSpecialty } from '../contexts/SpecialtyContext';
import { useAuth } from '../contexts/AuthContext';

const SpecialtySelection: React.FC = () => {
  const navigate = useNavigate();
  const { specialties, isLoading, setSelectedSpecialty } = useSpecialty();
  const { user, logout } = useAuth();

  const handleSpecialtySelect = (specialtyId: string) => {
    setSelectedSpecialty(specialtyId);
    navigate('/dashboard');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (isLoading) {
    return (
      <Container fluid className="specialty-page min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">Caricamento...</span>
          </Spinner>
          <p className="text-muted">Caricamento specialistiche...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="specialty-page min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="w-100" style={{ maxWidth: '900px' }}>
        <div className="text-center mb-5">
          <h1 className="display-4 fw-bold text-primary mb-3">
            Seleziona Specialistica
          </h1>
          <p className="lead text-muted">
            Benvenuto, {user?.first_name || 'Dottore'}! Scegli la tua specialistica per accedere alla dashboard.
          </p>
        </div>

        <Row className="g-4">
          {specialties.map((specialty) => (
            <Col key={specialty.id} md={6} lg={4}>
              <Card 
                className={`h-100 shadow-sm border-0 specialty-card ${!specialty.is_active ? 'opacity-50' : ''}`}
                style={{ 
                  cursor: specialty.is_active ? 'pointer' : 'not-allowed',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  if (specialty.is_active) {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (specialty.is_active) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                  }
                }}
                onClick={() => {
                  if (specialty.is_active) {
                    handleSpecialtySelect(specialty.id.toString());
                  }
                }}
              >
                <Card.Body className="text-center p-4">
                  <div className="mb-3">
                    <span 
                      style={{ fontSize: '3rem' }}
                      className="d-block mb-2"
                    >
                      {specialty.icon}
                    </span>
                  </div>
                  
                  <Card.Title className={`h4 text-${specialty.color} mb-3`}>
                    {specialty.name}
                  </Card.Title>
                  
                  <Card.Text className="text-muted mb-4">
                    {specialty.description}
                  </Card.Text>

                  <div className="mb-3">
                    {!specialty.is_active ? (
                      <small className="text-muted fw-bold">
                        ❌ Non Disponibile
                      </small>
                    ) : (
                      <div className="d-flex flex-column gap-1">
                        {specialty.has_fatturazione && (
                          <small className="text-success fw-bold">
                            ✅ Fatturazione Disponibile
                          </small>
                        )}
                        {specialty.has_refertazione && (
                          <small className="text-success fw-bold">
                            ✅ Refertazione Disponibile
                          </small>
                        )}
                        {!specialty.has_fatturazione && !specialty.has_refertazione && (
                          <small className="text-danger fw-bold">
                            ❌ Nessun Servizio Disponibile
                          </small>
                        )}
                        {!specialty.has_fatturazione && specialty.has_refertazione && (
                          <small className="text-warning fw-bold">
                            ⚠️ Solo Refertazione
                          </small>
                        )}
                        {specialty.has_fatturazione && !specialty.has_refertazione && (
                          <small className="text-warning fw-bold">
                            ⚠️ Solo Fatturazione
                          </small>
                        )}
                      </div>
                    )}
                  </div>

                  <Button 
                    variant={specialty.is_active ? specialty.color : 'secondary'}
                    size="lg"
                    className="w-100"
                    disabled={!specialty.is_active}
                  >
                    {specialty.is_active ? 'Seleziona' : 'Non Disponibile'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <div className="text-center mt-5">
          <Button 
            variant="outline-secondary"
            onClick={handleLogout}
            className="me-3"
          >
            Logout
          </Button>
          <small className="text-muted">
            Puoi cambiare specialistica in qualsiasi momento dalle impostazioni
          </small>
        </div>
      </div>
    </Container>
  );
};

export default SpecialtySelection;
